<script lang="ts" setup>
import useStore from "@src/stores/store";
import {computed, ref} from "vue";
import AttachLocation from "@src/components/views/HomeView/MainArea/FooterArea/AttachSelector/AttachLocation.vue";
import AttachContact from "@src/components/views/HomeView/MainArea/FooterArea/AttachSelector/AttachContact.vue";
import AttachCamera from "@src/components/views/HomeView/MainArea/FooterArea/AttachSelector/AttachCamera.vue";
import {useMessageStore} from "@src/stores/messages";

const props = defineProps(['icon', 'text', 'color', 'value', 'id', 'accept'])
const store = useStore();
const messageStore = useMessageStore();
const openLocationModal = ref<boolean>(false);
const openContactModal = ref<boolean>(false)
const openCameraModal = ref<boolean>(false)
const inputRef = ref<HTMLInputElement | null>(null);
const selectFile = () => {
    if (inputRef.value) {
        inputRef.value.value = '';
        inputRef.value.click();
    } else {
        if (props.value === 'location') {
            openLocationModal.value = true;
        }
        if (props.value === 'contact') {
            openContactModal.value = true;
        }
        if (props.value === 'camera') {
            openCameraModal.value = true;
        }
    }
}

const handleFileChange = (event: any) => {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    if (files && files.length > 0) {
        messageStore.submitMedia(Array.from(files), props.value, ext.value)
    }

    target.value = '';
};

const ext = computed(() => {
    switch (props.value) {
        case 'video':
            return ['mp4'];
        case 'audio':
            return ['mp3', 'wav', 'ogg'];
        case 'document':
            return [
                'aac',   // audio/aac
                'mp4',   // audio/mp4, video/mp4
                'mpeg',  // audio/mpeg
                'amr',   // audio/amr
                'ogg',   // audio/ogg
                'opus',  // audio/opus
                'ppt',   // application/vnd.ms-powerpoint
                'doc',   // application/msword
                'docx',  // application/vnd.openxmlformats-officedocument.wordprocessingml.document
                'pptx',  // application/vnd.openxmlformats-officedocument.presentationml.presentation
                'xlsx',  // application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
                'pdf',   // application/pdf
                'txt',   // text/plain
                'xls',   // application/vnd.ms-excel
                'jpg',   // image/jpeg
                'jpeg',  // image/jpeg
                'png',   // image/png
                'webp',  // image/webp
                '3gp',   // video/3gpp
            ];
        default:
            return ['png', 'jpg', 'jpeg'];
    }
})
</script>

<template>
    <div
        class="option-item flex items-center justify-start gap-3 mb-3 cursor-pointer"
        @click="selectFile"
    >
        <button
            :class="props.color"
            class="p-0 w-7 h-7 rounded-full cursor-pointer shadow-lg transition ease-in duration-200 focus:outline-none flex items-center justify-center active:shadow-lg">
            <component :is="props.icon" class="w-5 h-5 text-white"/>
        </button>
        <div
            :class="store.settings.darkMode ? 'hover:bg-gray-100 text-white' : 'hover:bg-gray-100'"
            class="bg-gray-100 shadow-lg rounded-lg px-4 py-2 text-sm font-medium dark:bg-gray-700 text-gray-700 whitespace-nowrap transition-all duration-200">
            {{ props.text }}
        </div>

        <input
            v-if="props.id"
            :id="props.id"
            ref="inputRef"
            :accept="props.accept ?? '*/*'"
            :multiple="props.value === 'image'"
            name="file"
            style="display: none;"
            type="file"
            @change="handleFileChange"
        >
        <Teleport to="#modal-root">
            <Transition
                v-if="props.value === 'location' && openLocationModal"
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-2">
                <AttachLocation @closeModal="openLocationModal = false"/>
            </Transition>
            <Transition
                v-else-if="openContactModal"
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-2">
                <AttachContact @closeModal="openContactModal = false"/>
            </Transition>
            <Transition
                v-else-if="props.value === 'camera' && openCameraModal"
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-2">
                <AttachCamera @closeModal="openCameraModal = false"/>
            </Transition>
        </Teleport>
    </div>
</template>

<style>
.option-item {
    transition: all 0.3s ease;
}

.option-item:hover {
    transform: translateX(-5px);
}

.option-item:hover > div {
    transform: scale(1.05);
}

.option-item:hover > button {
    transform: scale(1.1);
}

.option-item > div,
.option-item > button {
    transition: transform 0.2s ease;
}
</style>